import { FormBlock, FormCard } from '@qbe/ads-blocks-forms';
import { Box, Button, Form, Spinner } from '@qbe/ads-core';
import React, { useEffect } from 'react';
import { FormProvider } from 'react-hook-form';

import ABN from '~/components/ABN';
import BusinessName from '~/components/BusinessName';
import ContactDetails from '~/components/ContactDetails';
import ExtraInfoInterestedParties from '~/components/ExtraInfoInterestedParties';
import { useSnackbar } from '~/components-ds/Snackbar';
import { REVIEW_ROUTE } from '~/constants/routes';
import { useAppContent, useAppDispatch, useAppNavigate, useExtraInfo } from '~/hooks';
import { useAbn } from '~/hooks/useAbn';
import { useFormWithAnalytics } from '~/hooks/useFormWithAnalytics';
import { useTrackOptimizeActivate } from '~/hooks/useTrackOptimizeActivate';
import { hardDecline } from '~/slices/decline';
import {
  updateBusinessName,
  updateEmail,
  updateFirstName,
  updateLandlordName,
  updateLastName,
  updateMobile,
  updatePartnerMembershipNumber,
} from '~/slices/extraInfo';
import { updatePremiums } from '~/slices/quote';
import { qalFormCompleteAction, qalFormStartAction } from '~/thunks/analytics';
import { navigateAction } from '~/thunks/navigateAction';
import { quotePremiumsAction } from '~/thunks/quotePremiumsAction';
import { isFormStartStarted } from '~/utils/analytics/trackUtils';

import { LandlordDetails } from './LandlordDetails';
import { ExtraInfoFormData } from './types';

const ExtraInfoMoreBusinessDetails: React.FC = () => {
  const { getTextContent: getSharedContent } = useAppContent({
    basePath: 'shared',
  });

  const { getTextContent } = useAppContent({
    basePath: 'dsme.extraInfoMoreBusinessDetails',
  });

  const { getTextContent: getTextContentLandlord } = useAppContent({
    basePath: 'dsme.extraInfoMoreBusinessDetailsLandlord',
  });

  const { number: abn, businessNameLookup, noAbn } = useAbn();
  const {
    businessName,
    firstName,
    lastName,
    mobile,
    email,
    landlordName,
    partnerMembershipNumber,
  } = useExtraInfo();
  const dispatch = useAppDispatch();
  const form = useFormWithAnalytics<ExtraInfoFormData>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      abn: abn ?? '',
      noAbn,
      businessName: businessName ?? businessNameLookup.value ?? '',
      firstName: firstName ?? '',
      lastName: lastName ?? '',
      mobileNumber: mobile ?? '',
      email: email ?? '',
      needLandlordDetails: !!landlordName,
      landlordName: landlordName ?? '',
      partnerMembershipNumber: partnerMembershipNumber ?? '',
    },
  });
  const { setValue, trigger, handleSubmit, control, setFocus } = form;
  const navigate = useAppNavigate();

  const { enqueueSnackbar } = useSnackbar();

  const handleFormStart = () => {
    if (!isFormStartStarted()) dispatch(qalFormStartAction());
  };

  const handleFormComplete = () => {
    if (isFormStartStarted()) dispatch(qalFormCompleteAction());
  };
  useEffect(() => {
    if (businessNameLookup.value) {
      setValue('businessName', businessNameLookup.value);
      trigger('businessName').then();
    }
  }, [businessNameLookup.value, setValue, trigger]);

  const handleOnNoAbn = async (noAbnChecked: boolean) => {
    handleFormStart();
    if (noAbnChecked) {
      navigate('#business-name');
    }
  };

  const onSubmit = handleSubmit(
    async (formData) => {
      dispatch(updateBusinessName(formData.businessName));
      dispatch(updateFirstName(formData.firstName));
      dispatch(updateLastName(formData.lastName));
      dispatch(updateMobile(formData.mobileNumber));
      dispatch(updateEmail(formData.email));
      dispatch(updateLandlordName(formData.landlordName));
      dispatch(updatePartnerMembershipNumber(formData.partnerMembershipNumber));
      const data = await dispatch(quotePremiumsAction()).unwrap();
      if (data) {
        const { policyTransactionInformation } = data.policy.policyHeader;
        /**
         * @clarify should the absence of policyMessageStatus be enough to confirm isReject
         * or should we do policyMessageStatus === 'UNSUCCESSFUL' etc.
         * */
        const isReject = !!policyTransactionInformation?.policyMessageStatus;
        if (isReject) {
          const errors = data.policy.policyHeader.policyTransactionInformation.extendedStatus;
          errors && dispatch(hardDecline(errors));
        } else {
          dispatch(updatePremiums(data));
          handleFormComplete();
          dispatch(
            navigateAction({
              to: REVIEW_ROUTE,
              navigate,
            })
          );
        }
      }
    },
    (errors) => {
      const firstError = (Object.keys(errors) as Array<keyof typeof errors>).reduce<
        keyof typeof errors | null
      >((field, a) => {
        const fieldKey = field as keyof typeof errors;
        return errors[fieldKey] ? fieldKey : a;
      }, null);

      if (firstError && firstError !== 'root' && firstError !== 'form') {
        setFocus(firstError);
      }
      enqueueSnackbar(getTextContent('fields.submitValidationSnackbar.errorText'), {
        variant: 'error',
      });
    }
  );

  // Tracks the "optimize.activate" event when the component mounts or re-renders.
  // This is used to notify Webtrends (or other analytics tools) for visibility tracking
  // and ensures dynamic content is properly handled.
  useTrackOptimizeActivate();

  return (
    <FormProvider<ExtraInfoFormData> {...form}>
      <Spinner
        loading={form.formState.isSubmitting}
        description={getSharedContent('loadingLabel')}
      />

      <Form onSubmit={onSubmit} noValidate>
        <FormCard
          title={getTextContent('title')}
          variant="sectionTitle"
          data-testid="more-business-details"
        >
          <ABN onNoAbn={handleOnNoAbn} />

          <BusinessName control={control} name="businessName" />

          <ContactDetails control={control} />
        </FormCard>

        <FormCard
          variant="sectionTitle"
          title={getTextContentLandlord('title')}
          id="interested-third-parties"
        >
          <LandlordDetails />

          <ExtraInfoInterestedParties />

          <Box id="review-and-pay">
            <FormBlock preset="button">
              <Button
                label={getTextContent('buttons.reviewAndBuy')}
                type="submit"
                disabled={form.formState.isSubmitting || !form.formState.isValid}
              />
            </FormBlock>
          </Box>
        </FormCard>
      </Form>
    </FormProvider>
  );
};

export default ExtraInfoMoreBusinessDetails;
